import React, { useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { inject, observer } from 'mobx-react';

import CustomCol from '../../bootstrap/CustomCol';
import { modelOf } from '../../../prop-types';
import UIStore from '../../../store/UIStore';

// Matches $spacer (1rem) for gap between sidebar and viewport edges
const STICKY_TOP_OFFSET_PX = 16;
const BOTTOM_SPACER_PX = 16;
// Min header height fallback when uiStore.headerHeight not yet set (matches CSS fallback $spacer * 4)
const MIN_HEADER_HEIGHT_PX = 64;
// Unstick from bottom only after scrolling back up this many px (avoids bounce)
const STICKY_HYSTERESIS_PX = 80;
// Px before threshold where we start fixing and follow scroll (smooth transition to bottom stick)
const BOTTOM_TRANSITION_ZONE_PX = 120;

const RightSidebarLayout = ({
  uiStore,
  sidebar,
  children,
  sidebarPlaceHolder,
  contentPlaceHolder,
  sidebarStickyBottom = false,
}) => {
  const rightInnerRef = useRef(null);
  const sidebarContentRef = useRef(null);
  const spacerRef = useRef(null);
  const wasStuckAtBottomRef = useRef(false);
  const stuckAtScrollTopRef = useRef(0);

  const updateSidebarSticky = useCallback(() => {
    if (
      !sidebarStickyBottom ||
      !rightInnerRef.current ||
      !sidebarContentRef.current ||
      !spacerRef.current
    ) {
      return;
    }
    const sidebarEl = rightInnerRef.current;
    const contentEl = sidebarContentRef.current;
    const spacerEl = spacerRef.current;

    const scrollTop =
      window.scrollY ?? document.documentElement?.scrollTop ?? 0;
    const viewportHeight = window.innerHeight;
    const contentHeight = contentEl.getBoundingClientRect().height;
    const sidebarTop = sidebarEl.getBoundingClientRect().top + scrollTop;
    const sidebarRect = sidebarEl.getBoundingClientRect();

    const headerHeight = Math.max(
      Number(uiStore.headerHeight) || 0,
      MIN_HEADER_HEIGHT_PX
    );
    const fixedTop = headerHeight + STICKY_TOP_OFFSET_PX;

    const thresholdBottom = sidebarTop + contentHeight - viewportHeight;
    const thresholdTop = sidebarTop - fixedTop;
    const nearPageTop = scrollTop <= fixedTop;

    const currentlyStuckAtBottom = wasStuckAtBottomRef.current;
    const fullyStuckAtBottom =
      thresholdBottom > 0 &&
      (currentlyStuckAtBottom
        ? scrollTop >= stuckAtScrollTopRef.current - STICKY_HYSTERESIS_PX
        : scrollTop >= thresholdBottom);
    const inBottomTransitionZone =
      thresholdBottom > 0 &&
      !nearPageTop &&
      scrollTop >= thresholdBottom - BOTTOM_TRANSITION_ZONE_PX &&
      scrollTop < thresholdBottom &&
      !currentlyStuckAtBottom;
    const stickAtBottom = fullyStuckAtBottom || inBottomTransitionZone;

    if (fullyStuckAtBottom && !currentlyStuckAtBottom) {
      stuckAtScrollTopRef.current = scrollTop;
    }
    wasStuckAtBottomRef.current = fullyStuckAtBottom;

    const stickAtTop = !stickAtBottom && scrollTop >= thresholdTop;

    const mode = stickAtBottom
      ? inBottomTransitionZone
        ? 'stick-bottom-transition'
        : 'stick-bottom'
      : stickAtTop
      ? 'stick-top'
      : 'flow';

    if (mode === 'flow') {
      spacerEl.style.height = '';
      contentEl.style.position = '';
      contentEl.style.left = '';
      contentEl.style.width = '';
      contentEl.style.transform = '';
      contentEl.style.removeProperty('top');
      contentEl.style.removeProperty('bottom');
    } else {
      spacerEl.style.height = `${contentHeight}px`;
      contentEl.style.position = 'fixed';
      contentEl.style.left = `${sidebarRect.left}px`;
      contentEl.style.width = `${sidebarRect.width}px`;
      if (mode === 'stick-top') {
        contentEl.style.top = `${fixedTop}px`;
        contentEl.style.bottom = 'auto';
        contentEl.style.transform = '';
      } else {
        contentEl.style.bottom = `${BOTTOM_SPACER_PX}px`;
        contentEl.style.top = 'auto';
        const translateY =
          mode === 'stick-bottom-transition'
            ? Math.max(0, thresholdBottom - scrollTop)
            : 0;
        contentEl.style.transform = translateY
          ? `translateY(${translateY}px)`
          : '';
      }
    }
  }, [sidebarStickyBottom, uiStore.headerHeight]);

  useEffect(() => {
    if (!sidebarStickyBottom) return;
    let rafId = null;
    let scrollEndTimer = null;
    const runUpdate = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateSidebarSticky();
      });
    };
    const onScroll = () => {
      runUpdate();
      if (scrollEndTimer != null) clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(runUpdate, 150);
    };
    runUpdate();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', runUpdate);
    // When dragging the scrollbar, scroll events can be sparse; poll so we correct state after user releases at top
    const pollInterval = setInterval(runUpdate, 400);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', runUpdate);
      clearInterval(pollInterval);
      if (rafId != null) cancelAnimationFrame(rafId);
      if (scrollEndTimer != null) clearTimeout(scrollEndTimer);
    };
  }, [sidebarStickyBottom, updateSidebarSticky]);

  const renderCustomCol = (node, sizes, placeholder = null) => {
    return <CustomCol {...sizes}>{node || placeholder}</CustomCol>;
  };

  const sidebarNode = sidebar || sidebarPlaceHolder;
  const hasSidebar = Boolean(sidebarNode);

  const renderSidebar = () => {
    if (!sidebarNode) return null;

    if (sidebarStickyBottom) {
      return (
        <div ref={rightInnerRef} className="RightSidebarLayout__rightInner">
          <div
            ref={spacerRef}
            className="RightSidebarLayout__sidebarSpacer"
            aria-hidden="true"
          />
          <div
            ref={sidebarContentRef}
            className="RightSidebarLayout__sidebarContent"
          >
            {sidebarNode}
          </div>
        </div>
      );
    }
    return sidebarNode;
  };

  const leftColProps = hasSidebar
    ? {
        md: 12,
        lg: 8,
        xxl: 9,
        className: 'RightSidebarLayout__left',
      }
    : {
        md: 12,
        lg: 12,
        xxl: 12,
        className:
          'RightSidebarLayout__left RightSidebarLayout__left--fullWidth',
      };

  return (
    <>
      {renderCustomCol(children, leftColProps, contentPlaceHolder)}
      {hasSidebar &&
        renderCustomCol(
          renderSidebar(),
          {
            md: 12,
            lg: 4,
            xxl: 3,
            className: `RightSidebarLayout__right${
              sidebarStickyBottom
                ? ' RightSidebarLayout__right--stickyBottom'
                : ''
            }`,
            style: {
              '--header-height': `${uiStore.headerHeight}px`,
            },
          },
          sidebarPlaceHolder
        )}
    </>
  );
};

RightSidebarLayout.propTypes = {
  uiStore: modelOf(UIStore).isRequired,
  children: PropTypes.node,
  sidebar: PropTypes.node,
  contentPlaceHolder: PropTypes.node,
  sidebarPlaceHolder: PropTypes.node,
  /** When true, sidebar scrolls with page and becomes fixed with bottom at viewport bottom when its bottom would leave the viewport. */
  sidebarStickyBottom: PropTypes.bool,
};

export default inject('uiStore')(observer(RightSidebarLayout));
